/**
 * Validates a field based on its attributes and returns an error message if invalid.
 *
 * @param {Field} field - The field to validate.
 * @param {string} value - The value of the field.
 * @param {string} customErrorText - Custom error text to display for validation errors.
 * @returns {string|null} - The error message or null if the field is valid.
 */
function validateField(field, customErrorText, patternErrorText) {
  const value = resolveFieldValue(field);
  const requiredError = checkFieldRequired(field, value, customErrorText);
  if (requiredError) {
    return requiredError;
  }

  const patternError = checkInputPattern(field, value, patternErrorText || customErrorText);
  if (patternError) {
    return patternError;
  }

  const emailError = checkValidEmail(field, value);
  if (emailError) {
    return emailError;
  }

  const phoneError = checkValidPhoneNumber(field, value);
  if (phoneError) {
    return phoneError;
  }
  return null;
}

/**
 * Resolves the value of a form field, handling radio groups and checkbox groups.
 *
 * @param {HTMLInputElement} field - The field to resolve the value for.
 * @returns {string|string[]} - The resolved value, or an array of values for checkbox groups.
 */
function resolveFieldValue(field) {
  const { type, name, checked, value } = field;
  const form = field.closest("form");

  if (!form) {
    return checked ? [value] : "";
  }

  const group = form.querySelectorAll(`input[type="${type}"][name="${name}"]`);

  if (type === "radio") {
    const selected = Array.from(group).find((radio) => radio.checked);
    return selected ? selected.value : "";
  } else if (type === "checkbox") {
    const checkedValues = Array.from(group)
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => checkbox.value);
    return checkedValues.length ? checkedValues : "";
  } else {
    return value.trim();
  }
}

/**
 * Checks if a field is required and if the value is empty.
 *
 * @param {Field} field - The field to check.
 * @param {string} value - The value of the field.
 * @param {string} customErrorText - Custom error text to display.
 * @returns {string|null} - The error message or null if valid.
 */
function checkFieldRequired(field, value, customErrorText) {
  if (field.hasAttribute("required") && value === "") {
    return customErrorText || "This field is required.";
  }
  return null;
}

/**
 * Checks if a field value matches a specified pattern.
 *
 * @param {Field} field - The field to check.
 * @param {string} value - The value of the field.
 * @param {string} customErrorText - Custom error text to display.
 * @returns {string|null} - The error message or null if valid.
 */
function checkInputPattern(field, value, customErrorText) {
  const pattern = field.getAttribute("pattern");
  if (pattern && value !== "") {
    const patternRegex = new RegExp(`^(?:${pattern})$`, "u");
    if (!patternRegex.test(value)) {
      return customErrorText || "Invalid value.";
    }
  }
  return null;
}

/**
 * Checks if a field value is a valid email address.
 *
 * @param {Field} field - The field to check.
 * @param {string} value - The value of the field.
 * @returns {string|null} - The error message or null if valid.
 */
function checkValidEmail(field, value) {
  if (field.type === "email" && value !== "") {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(value)) {
      return "Please enter an email with a valid email format (e.g. yourname@example.com).";
    }
  }
  return null;
}

/**
 * Checks if a field value is a valid phone number.
 *
 * @param {Field} field - The field to check.
 * @param {string} value - The value of the field.
 * @returns {string|null} - The error message or null if valid.
 */
function checkValidPhoneNumber(field, value) {
  if (field.type === "tel" && value !== "") {
    const phonePattern = /^[\d\s+()-]+$/;
    if (!phonePattern.test(value)) {
      return "Please enter a valid phone number.";
    }
  }
  return null;
}

export { validateField };
